import React from 'react';
import { motion } from 'framer-motion';
import type { MenuItem } from './types';

interface SidebarProps {
  menuItems: MenuItem[];
  activeSection: string;
  onSectionChange: (section: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ menuItems, activeSection, onSectionChange }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="sticky top-24 relative bg-card/30 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-white/5 overflow-hidden"
    >
      {/* Padrão de fundo hexagonal */}
      <div className="absolute inset-0 -z-10">
        {/* Gradiente base escuro */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background/95 to-background" />
        
        {/* Padrão hexagonal com brilho */}
        <div className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(60deg, transparent 25%, rgba(0,149,255,0.05) 50%, transparent 75%),
              linear-gradient(-60deg, transparent 25%, rgba(0,149,255,0.05) 50%, transparent 75%),
              linear-gradient(60deg, transparent 25%, rgba(0,149,255,0.05) 50%, transparent 75%),
              linear-gradient(-60deg, transparent 25%, rgba(0,149,255,0.05) 50%, transparent 75%)
            `,
            backgroundSize: '100px 173px',
            backgroundPosition: '0 0, 0 0, 50px 86.5px, 50px 86.5px'
          }}
        />

        {/* Pontos de brilho nos vértices */}
        <div className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at center, rgba(0,149,255,0.15) 1px, transparent 2px)`,
            backgroundSize: '50px 86.5px',
            backgroundPosition: '25px 43.25px'
          }}
        />

        {/* Efeito de brilho dinâmico */}
        <div className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(0,149,255,0.1) 0%, transparent 50%)'
          }}
        />

        {/* Linhas de conexão suaves */}
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(30deg, transparent 45%, rgba(0,149,255,0.1) 50%, transparent 55%),
              linear-gradient(-30deg, transparent 45%, rgba(0,149,255,0.1) 50%, transparent 55%)
            `,
            backgroundSize: '100px 173px'
          }}
        />
      </div>

      <nav className="relative">
        <ul className="space-y-2">
          {menuItems.map((item, index) => (
            <motion.li
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <button
                onClick={() => onSectionChange(item.section)}
                className={`relative w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all group ${
                  activeSection === item.section
                    ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                    : 'hover:bg-white/5 hover:shadow-lg hover:shadow-white/5'
                }`}
              >
                {/* Efeito de brilho no hover */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" 
                  style={{ 
                    backgroundSize: '200% 100%',
                    animation: 'shine 2s infinite'
                  }}
                />
                
                <motion.div
                  initial={false}
                  animate={{
                    scale: activeSection === item.section ? 1.1 : 1,
                    rotate: activeSection === item.section ? 360 : 0
                  }}
                  transition={{ type: "spring", stiffness: 260, damping: 20 }}
                >
                  <item.icon className="w-5 h-5" />
                </motion.div>
                <span className="font-medium">{item.label}</span>
                {activeSection === item.section && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute inset-0 bg-primary/10 rounded-xl -z-10"
                    initial={false}
                    transition={{ type: "spring", stiffness: 260, damping: 20 }}
                  />
                )}
              </button>
            </motion.li>
          ))}
        </ul>
      </nav>
    </motion.div>
  );
};

export default Sidebar;